/**
 * Duct Renderer - Draws HVAC ducts on canvas
 */

import { DUCT_TYPES, DUCT_DRAW_SETTINGS } from '../../constants/hvacOptions';

/**
 * Draw a single duct segment
 * Note: Canvas context should already have scale transform applied
 * All coordinates are in world space (inches)
 */
export function drawDuct(ctx, duct, scale, isSelected = false, isPreview = false) {
  if (!duct.points || duct.points.length < 2) return;

  const ductType = DUCT_TYPES[duct.type] || DUCT_TYPES.supply;
  const ductWidth = duct.size || 6; // Width in inches (world coords)
  const isFlexDuct = duct.ductShape === 'flex';

  ctx.save();

  // Set styles based on duct type
  const fillOpacity = isPreview ? 0.3 : 0.5;

  ctx.fillStyle = isSelected
    ? `rgba(0, 200, 255, ${fillOpacity})`
    : `${ductType.color}${Math.round(fillOpacity * 255).toString(16).padStart(2, '0')}`;
  ctx.strokeStyle = isSelected
    ? '#00c8ff'
    : ductType.strokeColor;
  // Line width needs to account for scale since we want consistent screen appearance
  ctx.lineWidth = (isSelected ? 3 : DUCT_DRAW_SETTINGS.strokeWidth) / scale;

  if (isFlexDuct) {
    // Draw flex duct as a series of connected circles/ovals
    drawFlexDuct(ctx, duct.points, ductWidth, scale, isSelected);
  } else {
    // Draw trunk duct as rectangles along the path
    drawTrunkDuct(ctx, duct.points, ductWidth, scale, isSelected);
  }

  // Draw duct size label
  if (!isPreview && duct.points.length >= 2) {
    drawDuctLabel(ctx, duct, scale);
  }

  // Draw selection handles if selected
  if (isSelected) {
    drawSelectionHandles(ctx, duct.points, scale);
  }

  ctx.restore();
}

/**
 * Draw trunk (rectangular) duct
 */
function drawTrunkDuct(ctx, points, ductWidth, scale, isSelected) {
  const halfWidth = ductWidth / 2;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    // Calculate perpendicular direction
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 1) continue;

    const perpX = (-dy / length) * halfWidth;
    const perpY = (dx / length) * halfWidth;

    // Use world coordinates directly (canvas transform handles scaling)
    const x1 = p1.x;
    const y1 = p1.y;
    const x2 = p2.x;
    const y2 = p2.y;

    // Draw filled rectangle
    ctx.beginPath();
    ctx.moveTo(x1 + perpX, y1 + perpY);
    ctx.lineTo(x2 + perpX, y2 + perpY);
    ctx.lineTo(x2 - perpX, y2 - perpY);
    ctx.lineTo(x1 - perpX, y1 - perpY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw center line for supply/return indication
    ctx.save();
    ctx.setLineDash([4 / scale, 4 / scale]); // Dash pattern in screen pixels
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1 / scale;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  }

  // Draw corner joints
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i];
    ctx.beginPath();
    ctx.arc(p.x, p.y, halfWidth, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Draw flex (round) duct with wavy appearance
 */
function drawFlexDuct(ctx, points, ductWidth, scale, isSelected) {
  const halfWidth = ductWidth / 2;

  // Draw the main flex path
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 1) continue;

    const perpX = (-dy / length) * halfWidth;
    const perpY = (dx / length) * halfWidth;

    // Use world coordinates directly
    const x1 = p1.x;
    const y1 = p1.y;
    const x2 = p2.x;
    const y2 = p2.y;

    // Draw wavy edges for flex duct
    ctx.save();

    // Main body
    ctx.beginPath();
    ctx.moveTo(x1 + perpX, y1 + perpY);
    ctx.lineTo(x2 + perpX, y2 + perpY);
    ctx.lineTo(x2 - perpX, y2 - perpY);
    ctx.lineTo(x1 - perpX, y1 - perpY);
    ctx.closePath();
    ctx.fill();

    // Draw ribbed pattern - spacing in world units (inches)
    const ribSpacing = 6; // 6 inches between ribs
    const numRibs = Math.floor(length / ribSpacing);
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1 / scale;

    for (let r = 1; r < numRibs; r++) {
      const t = r / numRibs;
      const rx = x1 + (x2 - x1) * t;
      const ry = y1 + (y2 - y1) * t;

      ctx.beginPath();
      ctx.moveTo(rx + perpX * 0.9, ry + perpY * 0.9);
      ctx.lineTo(rx - perpX * 0.9, ry - perpY * 0.9);
      ctx.stroke();
    }

    // Outline
    ctx.lineWidth = 2 / scale;
    ctx.beginPath();
    ctx.moveTo(x1 + perpX, y1 + perpY);
    ctx.lineTo(x2 + perpX, y2 + perpY);
    ctx.moveTo(x1 - perpX, y1 - perpY);
    ctx.lineTo(x2 - perpX, y2 - perpY);
    ctx.stroke();

    ctx.restore();
  }

  // Draw end circles for flex duct
  if (points.length >= 2) {
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];

    ctx.beginPath();
    ctx.arc(firstPoint.x, firstPoint.y, halfWidth, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(lastPoint.x, lastPoint.y, halfWidth, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * Draw duct size label
 */
function drawDuctLabel(ctx, duct, scale) {
  const points = duct.points;
  if (points.length < 2) return;

  // Find midpoint of first segment (world coords)
  const midX = (points[0].x + points[1].x) / 2;
  const midY = (points[0].y + points[1].y) / 2;

  const label = `${duct.size}"`;

  ctx.save();
  // Font size needs to be in screen pixels, so divide by scale
  const fontSize = DUCT_DRAW_SETTINGS.labelFontSize / scale;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Background - sizes in world coords
  const textWidth = ctx.measureText(label).width + 6 / scale;
  const textHeight = fontSize + 4 / scale;
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(midX - textWidth / 2, midY - textHeight / 2, textWidth, textHeight);

  // Text
  ctx.fillStyle = '#fff';
  ctx.fillText(label, midX, midY);

  ctx.restore();
}

/**
 * Draw selection handles at duct points
 */
function drawSelectionHandles(ctx, points, scale) {
  // Handle size in screen pixels, converted to world units
  const handleSize = 6 / scale;

  ctx.fillStyle = '#00c8ff';
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1 / scale;

  points.forEach((point) => {
    const x = point.x;
    const y = point.y;

    ctx.beginPath();
    ctx.rect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
    ctx.fill();
    ctx.stroke();
  });
}

/**
 * Draw all ducts
 */
export function drawAllDucts(ctx, ducts, scale, selectedDuctId = null) {
  if (!ducts || ducts.length === 0) return;

  // Draw non-selected ducts first
  ducts.forEach((duct) => {
    if (duct.id !== selectedDuctId) {
      drawDuct(ctx, duct, scale, false, false);
    }
  });

  // Draw selected duct on top
  const selectedDuct = ducts.find((d) => d.id === selectedDuctId);
  if (selectedDuct) {
    drawDuct(ctx, selectedDuct, scale, true, false);
  }
}

/**
 * Draw duct preview while drawing
 */
export function drawDuctPreview(ctx, points, ductType, ductShape, ductSize, scale) {
  if (!points || points.length < 2) return;

  const previewDuct = {
    type: ductType,
    ductShape: ductShape,
    size: ductSize,
    points: points,
  };

  drawDuct(ctx, previewDuct, scale, false, true);
}

/**
 * Hit test for duct selection
 * @param {number} x - World X coordinate (in inches)
 * @param {number} y - World Y coordinate (in inches)
 * @param {Object} duct - Duct object with points in world coordinates
 * @param {number} scale - Current zoom scale (used to adjust hit tolerance)
 */
export function hitTestDuct(x, y, duct, scale) {
  if (!duct.points || duct.points.length < 2) return false;

  // Hit distance in world coords - duct width plus padding
  // At low zoom, increase tolerance so ducts are still selectable
  const basePadding = 10; // 10 inches padding
  const scaledPadding = Math.max(basePadding, basePadding / scale);
  const ductWidth = duct.size || 6;
  const hitDistance = ductWidth / 2 + scaledPadding;

  for (let i = 0; i < duct.points.length - 1; i++) {
    const p1 = duct.points[i];
    const p2 = duct.points[i + 1];

    const dist = pointToSegmentDistance(x, y, p1.x, p1.y, p2.x, p2.y);
    if (dist <= hitDistance) {
      return true;
    }
  }

  return false;
}

/**
 * Distance from point to line segment
 */
function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const nearestX = x1 + t * dx;
  const nearestY = y1 + t * dy;

  return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
}

export default {
  drawDuct,
  drawAllDucts,
  drawDuctPreview,
  hitTestDuct,
};
